import {useEffect, useState} from "react";
import {Autocomplete, CircularProgress, Stack, TextField, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {InAppNotificationAction} from "../../../types/notificationType";
import {ServiceModule} from "../../../types/serviceModuleTypes";
import {serviceModulesApi} from "../../../api/serviceModulesApi";
import {useAppDispatch} from "../../../hooks/StoreHooks";
import {handleRequest} from "../../../store/utils/handleRequest";

interface Props {
    action: InAppNotificationAction;
    onChange: (action: InAppNotificationAction) => void;
    isCompanyWideAssigned: boolean;
    groupIds: number[];
    employeeUserIds: string[];
}

export default function ServiceModuleReferenceField({
                                                        action,
                                                        onChange,
                                                        isCompanyWideAssigned,
                                                        groupIds,
                                                        employeeUserIds,
                                                    }: Props) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [modules, setModules] = useState<ServiceModule[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchModules = async () => {
            setIsLoading(true);
            const result = await handleRequest(
                dispatch,
                "indirect",
                () => serviceModulesApi.getServiceModulesFilteredByVisibility(isCompanyWideAssigned, groupIds, employeeUserIds)
            );
            if (result.success()) {
                const fetchedModules = result.result ?? [];
                setModules(fetchedModules);
                if (action.reference && !fetchedModules.some(m => String(m.id) === action.reference)) {
                    onChange({ ...action, reference: undefined });
                }
            }
            setIsLoading(false);
        };
        void fetchModules();
    }, [isCompanyWideAssigned, groupIds, employeeUserIds, dispatch]);

    const selectedModule = modules.find(m => String(m.id) === action.reference) ?? null;

    return (
        <Autocomplete
            options={modules}
            loading={isLoading}
            value={selectedModule}
            getOptionLabel={(m) => m.name ?? String(m.id)}
            renderOption={(props, m) => (
                <li {...props}>
                    <Stack
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: 1,
                            alignItems: "center",
                        }}
                    >
                        <span>{m.name ?? String(m.id)}</span>
                        <Typography variant="caption" color="text.secondary">
                            {m.type}
                        </Typography>
                    </Stack>
                </li>
            )}
            onChange={(_, selected) => onChange({
                ...action,
                reference: selected ? String(selected.id) : undefined
            })}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={t("genericInAppNotificationsPage.action.reference.serviceModule")}
                    slotProps={{
                        ...params.slotProps,
                        input: {
                            ...params.slotProps.input,
                            endAdornment: (
                                <>
                                    {isLoading && <CircularProgress size={18} />}
                                    {params.slotProps.input?.endAdornment}
                                </>
                            ),
                        },
                    }}
                />
            )}
        />
    );
}